/**
 * Iter339 · Segment Merge Wizard — detects near-duplicate segments
 * and lets the admin merge them in a single click.
 *
 * Renders as a Dashboard card that only appears when there is at
 * least one suggestion. Each row shows the winner + loser + how many
 * leads would move; the merge button rewrites the lead documents and
 * refreshes both the card and the parent TaxonomyQualityCard.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GitMerge, Loader2, RefreshCw, ArrowRight, Sparkles } from "lucide-react";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SegmentMergeCard() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [pairs, setPairs] = useState([]);
  const [distinct, setDistinct] = useState(0);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(0.8);
  const [busyKey, setBusyKey] = useState(null);
  const [merged, setMerged] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/leads-finder/quality/merge-suggestions`,
        { params: { min_similarity: threshold } });
      setPairs(data.pairs || []);
      setDistinct(data.distinct_segments || 0);
      setMerged(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge scan failed");
    } finally { setLoading(false); }
  }, [threshold]);
  useEffect(() => { load(); }, [load]);

  const run = async (pair) => {
    const key = `${pair.winner}::${pair.loser}`;
    setBusyKey(key);
    try {
      const { data } = await axios.post(
        `${API}/leads-finder/quality/merge`,
        { winner: pair.winner, loser: pair.loser });
      toast.success(isPt
        ? `Fusionado · ${data.leads_moved} leads movidos para "${data.winner}"`
        : `Merged · ${data.leads_moved} leads moved to "${data.winner}"`);
      setMerged((prev) => new Set([...prev, key]));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally { setBusyKey(null); }
  };

  // Hide when nothing to do.
  if (!loading && pairs.length === 0) return null;

  return (
    <section className="al-card p-5" data-testid="segment-merge-card">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#7C3AED]/12">
              <GitMerge className="w-4 h-4 text-[#7C3AED]" />
            </span>
            <h3 className="text-[14px] font-bold">
              {isPt ? "Fusão de segmentos" : "Segment merge wizard"}
            </h3>
          </div>
          <p className="text-[12px] text-[var(--al-text-muted)]">
            {isPt
              ? `${pairs.length} pares duplicados detectados em ${distinct} segmentos distintos`
              : `${pairs.length} duplicate pairs across ${distinct} distinct segments`}
          </p>
        </div>
        <div className="inline-flex items-center gap-1">
          <span className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--al-text-muted)] mr-1">
            {isPt ? "Sim." : "Sim."}
          </span>
          {[0.7, 0.8, 0.9].map((v) => (
            <button key={v} type="button"
              data-testid={`merge-threshold-${Math.round(v * 100)}`}
              onClick={() => setThreshold(v)}
              className={`h-7 px-2.5 rounded text-[11px] font-bold border transition ${
                Math.abs(threshold - v) < 0.001
                  ? "bg-[#7C3AED] border-[#7C3AED] text-white"
                  : "border-[var(--al-border)] hover:border-[#7C3AED] text-[var(--al-text-muted)]"}`}>
              ≥{Math.round(v * 100)}%
            </button>
          ))}
          <button type="button" onClick={load} data-testid="merge-refresh"
            className="w-7 h-7 rounded border border-[var(--al-border)] hover:border-[#7C3AED] inline-flex items-center justify-center">
            {loading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="h-32 rounded bg-[var(--al-bg)] animate-pulse" />
      ) : (
        <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1"
             data-testid="merge-pairs">
          {pairs.map((p) => {
            const key = `${p.winner}::${p.loser}`;
            const isBusy = busyKey === key;
            const isDone = merged.has(key);
            return (
              <div key={key}
                data-testid={`merge-row-${key.replace(/[^a-z0-9]/gi, "_")}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                  isDone
                    ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                    : "border-[var(--al-border)] hover:border-[#7C3AED]"}`}>
                <div className="flex-1 min-w-0 flex items-center gap-1.5 text-[12px]">
                  <span className="font-bold truncate text-[#1B3A8A]">
                    {p.winner}
                  </span>
                  <span className="text-[10px] px-1 rounded bg-[#1B3A8A]/10 text-[#1B3A8A] tabular-nums shrink-0">
                    {p.winner_count}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-[var(--al-text-muted)] shrink-0 rotate-180" />
                  <span className="truncate text-[var(--al-text-muted)] line-through decoration-1">
                    {p.loser}
                  </span>
                  <span className="text-[10px] px-1 rounded bg-rose-500/10 text-rose-600 tabular-nums shrink-0">
                    {p.loser_count}
                  </span>
                </div>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#7C3AED]/10 text-[#7C3AED] tabular-nums shrink-0"
                      title={p.reason}>
                  {(p.similarity * 100).toFixed(0)}%
                </span>
                <button type="button"
                  onClick={() => run(p)}
                  disabled={isBusy || isDone}
                  data-testid={`merge-run-${key.replace(/[^a-z0-9]/gi, "_")}`}
                  className={`h-8 px-2.5 rounded-lg text-[11.5px] font-bold shrink-0 inline-flex items-center gap-1 ${
                    isDone
                      ? "bg-emerald-500 text-white"
                      : "bg-[#7C3AED] hover:bg-[#6D28D9] text-white disabled:opacity-60"}`}>
                  {isBusy
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : isDone
                    ? <Sparkles className="w-3.5 h-3.5" />
                    : <GitMerge className="w-3.5 h-3.5" />}
                  {isDone
                    ? (isPt ? "Feito" : "Done")
                    : (isPt ? "Fundir" : "Merge")}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
